import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Podcast as PodcastIcon } from "lucide-react";
import { toast } from "sonner";
import { AudioPlayer } from "@/components/viewers/AudioPlayer";
import {
  GenericError,
  PaywallCard,
  ViewerScaffold,
} from "@/components/viewers/ViewerScaffold";
import { useViewerAccess } from "@/components/viewers/ChapterAccessGate";
import { useSignedUrl, type Language } from "@/hooks/useSignedUrl";
import { useViewerTracker } from "@/hooks/useViewerTracker";
import { useMediaCompletion } from "@/hooks/useMediaCompletion";
import { cn } from "@/lib/cn";

const LANGUAGES: { id: Language; label: string; native: string }[] = [
  { id: "english", label: "English", native: "English" },
  { id: "hindi",   label: "Hindi",   native: "हिंदी" },
  { id: "kannada", label: "Kannada", native: "ಕನ್ನಡ" },
];

export function PodcastPage() {
  const { chapterId } = useParams<{ chapterId: string }>();
  const { access, paywall, shouldFetch } = useViewerAccess(chapterId, "Podcast");
  // No dwell floor: "90% of the duration actually played" already carries the
  // time proof, and useMediaCompletion ignores seeks so scrubbing to the end
  // counts for nothing.
  const { reportEndReached } = useViewerTracker({ chapterId, mode: "podcast", active: paywall === null });
  const handlePlaybackProgress = useMediaCompletion({ onComplete: reportEndReached });

  const [language, setLanguage] = useState<Language>("english");

  const { url, isLoading, isError, isLocked, isNotFound, refresh } = useSignedUrl({
    chapterId,
    assetType: "podcast",
    language,
    enabled: shouldFetch,
  });

  // When a language switch resolves to "not uploaded" (404), toast the
  // user, revert to English, and don't keep the broken selection. This
  // mirrors the v1 availability check but is now driven by the API
  // response instead of HEAD-probing GitHub.
  const [revertAfterMiss, setRevertAfterMiss] = useState<Language | null>(null);
  useEffect(() => {
    if (isNotFound && language !== "english") {
      const missed = language;
      toast.error(
        `${LANGUAGES.find((l) => l.id === missed)?.label} podcast is not available for this chapter`,
      );
      setRevertAfterMiss(missed);
      setLanguage("english");
    }
  }, [isNotFound, language]);

  const chapter = access.chapter;
  const classId = access.needsClassId ?? chapter?.class_id;

  const showPaywall = isLocked && !revertAfterMiss;

  return (
    <ViewerScaffold
      chapterTitle={chapter?.chapter_name ?? null}
      subjectName={null}
      modeLabel="Chapter Podcast"
      modeIcon={PodcastIcon}
      modeAccent="bg-pink-500/10 text-pink-600 dark:text-pink-400"
    >
      {paywall ?? (showPaywall ? (
        <PaywallCard modeLabel="Podcast" classId={classId} />
      ) : isError ? (
        <GenericError onRetry={refresh} />
      ) : (
        <div className="py-6 sm:py-8">
          <AudioPlayer
            // Re-mount when language changes so the audio element fully resets.
            key={`${chapterId}:${language}`}
            src={url ?? ""}
            progressKey={chapterId ? `${chapterId}:podcast:${language}` : undefined}
            onPlaybackProgress={handlePlaybackProgress}
            onError={() => {
              // Audio failed mid-stream (probably a stale signed URL --
              // refresh it; useSignedUrl re-fetches and the player remounts).
              refresh();
            }}
            topSlot={
              <LanguageSwitcher
                value={language}
                onChange={(next) => {
                  if (next === language) return;
                  setRevertAfterMiss(null);
                  setLanguage(next);
                }}
                loadingLanguage={isLoading ? language : null}
              />
            }
          />

          {isNotFound && language === "english" ? (
            <p className="mt-4 text-center text-sm text-text-muted">
              Podcast isn't uploaded for this chapter yet.
            </p>
          ) : null}
        </div>
      ))}
    </ViewerScaffold>
  );
}

function LanguageSwitcher({
  value,
  onChange,
  loadingLanguage,
}: {
  value: Language;
  onChange: (lang: Language) => void;
  loadingLanguage: Language | null;
}) {
  return (
    <div
      role="tablist"
      aria-label="Podcast language"
      className="mx-auto flex max-w-xs items-center gap-1 rounded-lg border border-border bg-surface-2 p-1"
    >
      {LANGUAGES.map((l) => {
        const active = l.id === value;
        const loading = loadingLanguage === l.id;
        return (
          <button
            key={l.id}
            role="tab"
            aria-selected={active}
            type="button"
            onClick={() => onChange(l.id)}
            className={cn(
              "flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              active
                ? "bg-brand text-white shadow-sm"
                : "text-text-muted hover:text-text hover:bg-surface",
              loading && "opacity-80",
            )}
          >
            {l.native}
          </button>
        );
      })}
    </div>
  );
}
